import { useQuery, useMutation, gql } from '@apollo/client';
import { useParams, useNavigate, useSearchParams, Navigate } from 'react-router-dom';
import { GET_TAGS as GET_POSSIBLE_TAGS } from '../../../queries/tags';
import { TagsPicker } from '../../../components/Protected/TagsPicker';
import { MediaTile } from '../../../components/MediaTile';
import { useContentReviewerGate } from '../../../hooks/isContentReviewer';
import { PATHS } from '../../../paths';
import { Message } from 'semantic-ui-react'

const GET_SINGLE_IMAGE = gql`
  query Image($imageId: Int!) {
    image(id: $imageId) {
      id
      url
      contentType
      durationSeconds
      tags {
        id
        groupId
        name
      }
      subjects {
        id
        user {
          name
        }
      }
    }
  }
`;

const GET_MEMBERS_I_CAN_TAG = gql`
  query MembersICanTag {
    membersICanTag {
      id
      user {
        name
      }
    }
  }
`;

const UPDATE_IMAGE_TAGS = gql`
  mutation UpdateImageTags($imageId: Int!, $tagIds: [Int!]!) {
    updateImageTags(imageId: $imageId, tagIds: $tagIds) {
      id
      name
    }
  }
`;

const UPDATE_MEMBER_IMAGES = gql`
  mutation UpdateMemberImages($imageId: Int!, $membershipIds: [Int!]!) {
    updateMemberImages(imageId: $imageId, membershipIds: $membershipIds) {
      id
      subjects {
        id
        user {
          name
        }
      }
    }
  }
`;

const ButtonStyles = {
    marginBottom: '20px',
    padding: '8px 16px',
    cursor: 'pointer',
    backgroundColor: '#fff',
    border: '1px solid #ccc',
    borderRadius: '4px',
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
}

// gallery/1/tags?nextImageIds=2,3,4 so when i am done with this it will go to the next image
// if there are nextImages then we need to add a next button that jumps to the next image and if
// there are none just say done and take us back to however you go to this page

function TagImage() {
    const { imageId } = useParams<{ imageId: string }>();
    const [searchParams, setSearchParams] = useSearchParams();
    const { loading: meLoading, isContentReviewer } = useContentReviewerGate();

    let nextImageIds: number[] = []

    const idNum = Number(imageId);
    const navigate = useNavigate();

    const {
        data: imageData,
        loading: imageLoading,
        refetch: refetchImage,
    } = useQuery(GET_SINGLE_IMAGE, {
        variables: { imageId: idNum },
    });

    const {
        data: allTagsData,
        loading: tagsLoading,
        error: tagsError,
    } = useQuery(GET_POSSIBLE_TAGS, {
        variables: {
            showPeopleTags: false,
            showCourseTags: true,
        },
    });

    const {
        data: membersData,
        loading: membersLoading,
        error: membersError,
    } = useQuery(GET_MEMBERS_I_CAN_TAG);

    const [updateImageTags] = useMutation(UPDATE_IMAGE_TAGS);
    const [updateMemberImages] = useMutation(UPDATE_MEMBER_IMAGES);

    const list = searchParams.get('nextImageIds');
    if (list != null) {
        const items = list.split(',')
        nextImageIds = items.map(Number)
    }

    // Content reviewers only — everyone else goes back to classes. Must come before the
    // "Image not found" branch below: the backend now returns null for non-reviewers, so
    // they would otherwise land on that message instead of being redirected.
    // Wait for GET_ME so a real reviewer isn't bounced during the initial load.
    if (!meLoading && !isContentReviewer) {
        return <Navigate to={PATHS.CLASSES} replace />;
    }

    if (imageLoading) return <div style={{ padding: '20px' }}>Loading Image...</div>;
    if (!imageData?.image) return <div style={{ padding: '20px' }}>Image not found</div>;

    const currentSubjects = imageData.image.subjects.map((s: any) => ({
        id: s.id,
        name: s.user?.name || 'Unknown User',
    }));

    const availableMembers = {
        tags: membersData?.membersICanTag?.map((m: any) => ({
            id: m.id,
            name: m.user?.name || 'Unknown Member',
        })) || [],
    };

    const scopes = imageData.image.tags.filter((t: any) => t.groupId === 2);

    return (
        <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '40px' }}>
            <button
                onClick={() => navigate(-1)}
                style={ButtonStyles}
            >
                ← Back
            </button>

            <div style={{ display: 'flex', gap: '30px', alignItems: 'flex-start' }}>
                <div
                    style={{
                        flex: '2',
                        textAlign: 'center',
                        backgroundColor: '#f5f5f5',
                        borderRadius: '8px',
                        overflow: 'hidden',
                        padding: '10px',
                    }}
                >
                    <MediaTile
                        url={imageData.image.url}
                        contentType={imageData.image.contentType}
                        durationSeconds={imageData.image.durationSeconds}
                        alt="Target"
                        fluid
                        controls
                        style={{
                            width: '100%',
                            height: 'auto',
                            display: 'block',
                            borderRadius: '4px',
                        }}
                    />
                </div>

                <div style={{ flex: '1', minWidth: '300px' }}>
                    <h3 style={{ marginTop: 0 }}>Image Management</h3>

                    <div style={{ marginBottom: '25px', padding: '15px', backgroundColor: '#f9f9f9', borderRadius: '6px', border: '1px solid #eee' }}>
                        <p style={{ fontWeight: 'bold', margin: '0 0 10px 0', fontSize: '0.9rem', color: '#555' }}>Communities with visibility</p>
                        {scopes.length > 0 ? (
                            <ul style={{ margin: 0, paddingLeft: '20px', color: '#333' }}>
                                {scopes.map((scope: any) => (
                                    <li key={scope.id} style={{ marginBottom: '4px' }}>{scope.name}</li>
                                ))}
                            </ul>
                        ) : (
                            <span style={{ fontSize: '0.85rem', color: '#999', fontStyle: 'italic' }}>No scopes selected</span>
                        )}
                    </div>

                    {scopes.length == 0 && (
                        <><Message>
                            <Message.Header>No scopes detected</Message.Header>
                            <p>You must add this images to at least one community</p>
                        </Message></>
                    )}

                    <div style={{ marginBottom: '30px' }}>
                        <p style={{ fontWeight: 'bold', marginBottom: '5px' }}>General Tags</p>
                        <TagsPicker
                            tags={imageData.image.tags}
                            refetchParent={refetchImage}
                            loading={tagsLoading}
                            error={tagsError}
                            data={allTagsData}
                            updateTags={updateImageTags}
                            payload={{ imageId: idNum }}
                        />
                    </div>

                    <hr style={{ border: 'none', borderTop: '1px solid #eee', margin: '30px 0' }} />

                    <div style={{ marginBottom: '20px' }}>
                        <p style={{ fontWeight: 'bold', marginBottom: '5px' }}>Associated Members</p>
                        <p style={{ color: '#666', fontSize: '0.85rem', marginBottom: '15px' }}>
                            Tag people that appear in this image.
                        </p>

                        <TagsPicker
                            tags={currentSubjects}
                            refetchParent={refetchImage}
                            loading={membersLoading}
                            error={membersError}
                            data={availableMembers}
                            updateTags={(options: any) => {
                                const ids = options.variables?.tagIds || [];
                                return updateMemberImages({
                                    variables: {
                                        imageId: idNum,
                                        membershipIds: ids,
                                    },
                                });
                            }}
                            payload={{ imageId: idNum }}
                        />
                    </div>
                </div>
            </div>
             <div style={{ float: 'right' }}>
            {nextImageIds.length != 0 &&
                <button
                    disabled={scopes.length == 0}
                    style={ButtonStyles}
                    onClick={() => {
                        let path = '';
                        if (nextImageIds.length > 0) {
                            path = `/gallery/${nextImageIds[0]}/tags`
                        }

                        if (nextImageIds.length > 1) {
                            nextImageIds.shift()
                            path = `${path}?nextImageIds=${nextImageIds.join(',')}`
                        }
                        navigate(path)

                    }}
                >
                    Done - next image
                </button>}
                {nextImageIds.length == 0 && <button
                    onClick={() => navigate('/gallery')}
                    style={ButtonStyles}
                >
                    Go to gallery
                </button>}
            </div>
        </div>
    );
}

export { TagImage };
